import React from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";

/**
 * /about — luxury brand narrative page (iter59).
 *
 * Five-section flow per Emergent Build Guide: Hero → Origin Story →
 * One Connection, Three Audiences → Philosophy → Vision + Final CTA.
 * Editorial layout, generous whitespace, alternating cream/white
 * backgrounds, restrained gold accents, deep-green closing panel.
 *
 * Copy is used verbatim from the spec. No feature lists, no counters,
 * no statistics — the page reads as a private-club brand story.
 */

// Refined private-club pro-shop / member lifestyle photo used in the
// Origin Story section. Same asset library the /vendors and /clubs
// pages draw from so brand imagery stays consistent.
const PRO_SHOP_IMG =
  "https://static.prod-images.emergentagent.com/jobs/e16202cb-95d0-411c-902c-bd0d02e3145b/images/f5bc4a7fe6e1676168c6e0591718eb78ed19e3f94cf3d783330b28fced0a79a2.png";

export default function AboutPage() {
  return (
    <MarketingLayout>
      <AboutHero />
      <OriginStory />
      <ThreeAudiences />
      <Philosophy />
      <VisionFinalCta />
    </MarketingLayout>
  );
}

// ============================================================================
// Section 1 — Hero
// ============================================================================
function AboutHero() {
  return (
    <section
      data-testid="about-hero"
      className="bg-[#F9F8F4]"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-14 py-28 sm:py-36">
        <div className="text-[12px] uppercase tracking-[0.34em] font-semibold text-[#B59A5A]">
          About My Crest Club
        </div>
        <h1
          data-testid="about-hero-headline"
          className="mt-8 font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A422D] leading-[1.05] max-w-4xl"
        >
          The private club experience should not end at the pro shop door.
        </h1>
        <p className="mt-10 text-lg sm:text-xl text-[#4A4A4A] leading-relaxed max-w-3xl">
          My Crest Club was created to help private clubs serve members beyond what happens to be available on the shop floor. We connect members, their club, and trusted vendors through one private platform built around access, personalization, and service.
        </p>
        <p className="mt-8 font-serif italic text-xl sm:text-2xl text-[#1A422D] max-w-2xl">
          Built for private clubs. Designed around their members.
        </p>

        <div className="mt-14 flex flex-wrap gap-4">
          <a
            href="#origin-story"
            data-testid="about-hero-explore"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("origin-story")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.28em] font-semibold hover:bg-[#0F2A1C] transition-colors"
          >
            Explore Our Story <span aria-hidden>↓</span>
          </a>
          <Link
            to="/contact"
            data-testid="about-hero-demo"
            className="inline-flex items-center gap-2 px-8 py-4 border border-[#1A422D] text-[#1A422D] text-[13px] uppercase tracking-[0.28em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
          >
            Schedule a Demo
          </Link>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Section 2 — Origin Story · emotional center · editorial 2-col
// ============================================================================
function OriginStory() {
  return (
    <section
      id="origin-story"
      data-testid="about-origin-section"
      className="bg-white border-y border-[#E5E3D8]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 py-28 sm:py-36 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        {/* Image */}
        <div className="order-1 lg:order-none">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src={PRO_SHOP_IMG}
              alt="Private club pro shop interior"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Story */}
        <div>
          <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#B59A5A]">
            It Started with a Member
          </div>
          <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A422D] leading-[1.08]">
            It started with a member who couldn&rsquo;t find his size.
          </h2>

          <div className="mt-10 space-y-5 text-[15px] sm:text-base text-[#4A4A4A] leading-relaxed max-w-xl">
            <p>
              The idea for My Crest Club began inside a private golf shop. A member was looking for an item in his size, but it was already gone. His frustration was simple: the shop rarely had his size when he wanted something. At the time, he was wearing another club&rsquo;s logo.
            </p>
            <p>
              That moment exposed a larger problem. Private clubs cannot stock every style, size, color, and product their members may want. Seasonal members miss new arrivals. Popular sizes sell out. And when a member discovers something elsewhere, there is often no simple way to ask, &ldquo;Can I get this with my club crest?&rdquo;
            </p>
            <p className="font-serif italic text-lg text-[#1A422D]">
              My Crest Club was built to close that gap.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Section 3 — One Connection, Three Audiences
// ============================================================================
function ThreeAudiences() {
  const items = [
    {
      label: "For Members",
      body: "Discover, request, and access merchandise through the club they already trust.",
    },
    {
      label: "For Clubs",
      body: "Extend the golf shop beyond physical inventory while maintaining control of the member relationship.",
    },
    {
      label: "For Vendors",
      body: "Build stronger connections with private clubs and better understand what their members actually want.",
    },
  ];
  return (
    <section
      data-testid="about-three-audiences"
      className="bg-[#F9F8F4]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 py-28 sm:py-36">
        <div className="max-w-3xl">
          <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#B59A5A]">
            How It Connects
          </div>
          <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A422D] leading-[1.08]">
            One private connection. Three sides of the club experience.
          </h2>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
          {items.map((it, i) => (
            <div
              key={it.label}
              data-testid={`about-audience-${i + 1}`}
              className="border-t border-[#D9C68A]/60 pt-8"
            >
              <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#B59A5A]">
                {it.label}
              </div>
              <p className="mt-6 text-[17px] leading-relaxed text-[#1A422D] font-serif">
                {it.body}
              </p>
            </div>
          ))}
        </div>

        {/* Signature statement — visually prominent */}
        <div
          data-testid="about-signature-statement"
          className="mt-28 max-w-4xl mx-auto text-center"
        >
          <div className="mx-auto w-16 h-px bg-[#D9C68A]" />
          <p className="mt-10 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A422D] leading-[1.15]">
            My Crest Club does not replace the golf shop.
            <br />
            <span className="text-[#B59A5A]">It gives the golf shop a longer reach.</span>
          </p>
          <div className="mx-auto w-16 h-px bg-[#D9C68A] mt-10" />
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Section 4 — Philosophy
// ============================================================================
function Philosophy() {
  const principles = [
    { label: "Anticipate", body: "Understand demand before the opportunity is lost." },
    { label: "Personalize", body: "Help members find what fits their individual tastes and preferences." },
    { label: "Connect", body: "Bring clubs, members, and vendors closer without disrupting the club relationship." },
  ];
  return (
    <section
      data-testid="about-philosophy-section"
      className="bg-white border-y border-[#E5E3D8]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 py-28 sm:py-36">
        <div className="max-w-3xl">
          <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#B59A5A]">
            Our Philosophy
          </div>
          <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A422D] leading-[1.08]">
            Built around a simple idea: better service begins with understanding what people want.
          </h2>
          <p className="mt-10 text-[15px] sm:text-base text-[#4A4A4A] leading-relaxed max-w-2xl">
            The best private clubs anticipate member needs before they have to ask twice. My Crest Club brings that same service philosophy to merchandise. Every request, preference, and interaction can help the club better understand its members, improve buying decisions, and create a more personal retail experience over time.
          </p>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
          {principles.map((p, i) => (
            <div
              key={p.label}
              data-testid={`about-principle-${i + 1}`}
              className="text-center md:text-left"
            >
              <div className="font-serif text-5xl lg:text-6xl text-[#D9C68A] leading-none">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="mt-8 text-[12px] uppercase tracking-[0.32em] font-semibold text-[#B59A5A]">
                {p.label}
              </div>
              <p className="mt-4 text-base text-[#1A422D] font-serif leading-snug">
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Section 5 — Vision + Final CTA
// ============================================================================
function VisionFinalCta() {
  return (
    <section
      data-testid="about-vision-cta"
      className="bg-[#1A422D] text-[#F9F8F4]"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-14 py-28 sm:py-36 text-center">
        <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#D9C68A]">
          Our Vision
        </div>
        <h2 className="mt-8 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.08] max-w-3xl mx-auto">
          A better way for private clubs to serve beyond the rack.
        </h2>
        <p className="mt-10 text-lg leading-relaxed text-[#F9F8F4]/85 max-w-2xl mx-auto">
          We believe the future of private club retail is not about carrying more inventory. It is about creating better access. My Crest Club is building the infrastructure that helps clubs respond to member demand, helps vendors better serve the private-club market, and gives members a more personal way to shop through the clubs they belong to.
        </p>

        <div className="mx-auto w-16 h-px bg-[#D9C68A]/50 mt-16" />

        <h3 className="mt-16 font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F9F8F4] leading-tight">
          See what My Crest Club can do for your club.
        </h3>

        <div className="mt-12 flex flex-wrap justify-center gap-4">
          <Link
            to="/clubs"
            data-testid="about-cta-clubs"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#D9C68A] text-[#1A422D] text-[13px] uppercase tracking-[0.28em] font-semibold hover:bg-[#E5D6A8] transition-colors"
          >
            For Clubs <span aria-hidden>→</span>
          </Link>
          <Link
            to="/vendors"
            data-testid="about-cta-vendors"
            className="inline-flex items-center gap-2 px-8 py-4 border border-[#D9C68A] text-[#F9F8F4] text-[13px] uppercase tracking-[0.28em] font-semibold hover:bg-[#D9C68A] hover:text-[#1A422D] transition-colors"
          >
            For Vendors <span aria-hidden>→</span>
          </Link>
          <Link
            to="/contact"
            data-testid="about-cta-demo"
            className="inline-flex items-center gap-2 px-8 py-4 border border-[#F9F8F4]/40 text-[#F9F8F4] text-[13px] uppercase tracking-[0.28em] font-semibold hover:bg-[#F9F8F4] hover:text-[#1A422D] transition-colors"
          >
            Schedule a Demo
          </Link>
        </div>

        <p
          data-testid="about-quiet-marketplace-line"
          className="mt-20 font-serif italic text-lg text-[#F9F8F4]/70 max-w-xl mx-auto"
        >
          The quiet marketplace behind the private-club experience.
        </p>
      </div>
    </section>
  );
}
